import { debounce } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import styled from 'styled-components'
import { Button as SutraButton } from 'sutra/components/Button'
import { Select } from 'sutra/components/Select/Select'

import { alertTypeOptions } from 'features/alerts/components/alert-type-options/AlertTypeOptions'
import {
  customLimitsList,
  intervalOptionsList,
  outlierAlertsList,
} from 'features/alerts/constants'
import type { AlertFilterState } from 'features/alerts/features/create-alert/alert-filter-state'
import { AlertChartPreview } from 'features/alerts/features/create-alert/components/alert-chart-preview/AlertChartPreview'
import AlertDefinition from 'features/alerts/features/create-alert/components/alert-definition/AlertDefinition'
import {
  ButtonSpinner,
  CustomButtonGroupItem,
  ErrorMessage,
  StyledInput,
  StyledNumberField,
} from 'features/alerts/features/create-alert/create-alert.styles'
import { AlertType } from 'features/alerts/service'
import type { FilterOption } from 'features/filters/types'
import mixpanel from 'trackers/mixpanel'

interface Props {
  control: Control<AlertType>
  errors: FieldErrors<AlertType>
  projectId: string
  recipients: string[]
  setRecipients: (recipients: string[]) => void
  owners: string[]
  setOwners: (owners: string[]) => void
  allUsers: string[]
  setValue: UseFormSetValue<AlertType>
  getValues: UseFormGetValues<AlertType>
  trigger: UseFormTrigger<AlertType>
  selectedTriggerCategory: string
  setSelectedTriggerCategory: (c: string) => void
  alert: AlertType | undefined
  filterState: AlertFilterState
  setFilterState: (filterState: AlertFilterState) => void
  filterOptions: FilterOption[]
  // chart props forwarded to AlertChartPreview inside the Magnitude section
  alert_scope: string
  compare_value: number
  time_interval: string
  trigger_type: string
  reviews_filter: number
  isFormValid: boolean
  chartData: unknown
  chartIsLoading: boolean
  isSubmitting: boolean
}

const getCompareValueBounds = (
  alertType: string,
  triggerType: string,
): { min?: number; max?: number } => {
  const isDelta = ['Increased', 'Decreased'].includes(triggerType)
  if (alertType === 'Star Rating') {
    return isDelta ? { min: 0, max: 4 } : { min: 1, max: 5 }
  }
  if (alertType === 'Sentiment') {
    return isDelta ? { min: 0, max: 2 } : { min: -1, max: 1 }
  }
  return { min: 1 }
}

const getMetricCaption = (alertType: string): string | null => {
  if (alertType === 'Star Rating') return 'Star Rating must be between 1 and 5'
  if (alertType === 'Sentiment') return 'Sentiment must be between -1 and 1'
  if (alertType === 'Volume') return 'Volume must be 1 or greater'
  return null
}

const getDefaultCompareValue = (alertType: string): number => {
  if (alertType === 'Star Rating') return 1
  if (alertType === 'Sentiment') return 0.2
  return 1
}

const AlertSummaryCard: React.FC<Props> = ({
  control,
  errors,
  projectId,
  recipients,
  setRecipients,
  owners,
  setOwners,
  allUsers,
  setValue,
  getValues,
  trigger,
  selectedTriggerCategory,
  setSelectedTriggerCategory,
  alert,
  filterState,
  setFilterState,
  filterOptions,
  alert_scope,
  compare_value,
  time_interval,
  trigger_type,
  reviews_filter,
  isFormValid,
  chartData,
  chartIsLoading,
  isSubmitting,
}) => {
  // ── local state mirrors (drive validation + volume label) ──────────────────
  const [alertType, setAlertType] = useState(
    alert ? alert.alert_type : alertTypeOptions[0]!.value,
  )
  const [triggerType, setTriggerType] = useState(
    alert ? alert.trigger_type : outlierAlertsList[0]!.value,
  )

  // re-validate compare_value whenever metric / condition / value changes
  const compareValue = getValues().compare_value
  useEffect(() => {
    trigger('compare_value')
  }, [triggerType, alertType, compareValue, trigger])

  // ── debounced mixpanel ─────────────────────────────────────────────────────
  const debouncedTrack = useMemo(
    () =>
      debounce((action: string, value: string) => {
        mixpanel.track('alerts', { action, value, proj_uuid: projectId })
      }, 1000),
    [projectId],
  )

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleAlertTypeChange = (value: string): void => {
    mixpanel.track('alerts', {
      action: 'alert type',
      value,
      proj_uuid: projectId,
    })
    setValue('alert_type', value)
    setValue('compare_value', getDefaultCompareValue(value))
    setAlertType(value)
  }

  const handleOutlierDetectionClick = (): void => {
    if (selectedTriggerCategory === 'outlier') return
    setSelectedTriggerCategory('outlier')
    setValue('trigger_type', 'Change')
    setTriggerType('Change')
    mixpanel.track('alerts', {
      action: 'trigger category',
      value: 'outlier',
      proj_uuid: projectId,
    })
  }

  const handleLimitClick = (): void => {
    if (selectedTriggerCategory === 'limit') return
    setSelectedTriggerCategory('limit')
    setValue('trigger_type', 'Above')
    setTriggerType('Above')
    mixpanel.track('alerts', {
      action: 'trigger category',
      value: 'limit',
      proj_uuid: projectId,
    })
  }

  const handleAlertConditionChange = (value: string): void => {
    mixpanel.track('alerts', {
      action: 'alert condition',
      value,
      proj_uuid: projectId,
    })
    setValue('trigger_type', value)
    setTriggerType(value)
  }

  const showCompareValue = selectedTriggerCategory === 'limit'
  const conditionOptions =
    selectedTriggerCategory === 'limit' ? customLimitsList : outlierAlertsList

  return (
    <SummaryCard>
      {/* ── Name ─────────────────────────────────────────────────────────── */}
      <SummaryRow>
        <RowLabelCol>
          <RowLabel>Name</RowLabel>
          <RowSubLabel>How it shows up in your list</RowSubLabel>
        </RowLabelCol>
        <RowControlCol>
          <Controller
            name="title"
            control={control}
            rules={{ required: 'Title is required' }}
            render={({ field }) => (
              <StyledInput
                {...field}
                onChange={(e) => {
                  field.onChange(e.target.value)
                  debouncedTrack('title', e.target.value)
                }}
                placeholder="Alert Title"
                aria-label="Alert Title"
                $isError={!!errors.title}
              />
            )}
          />
          {!!errors.title && <TitleError>Please name your alert</TitleError>}
        </RowControlCol>
      </SummaryRow>

      <RowDivider />

      {/* ── Owners ───────────────────────────────────────────────────────── */}
      <SummaryRow>
        <RowLabelCol>
          <RowLabel>Owners</RowLabel>
          <RowSubLabel>Can manage the alert — and receive it</RowSubLabel>
        </RowLabelCol>
        <RowControlCol>
          <RecipientsSelect
            mode="multiple"
            value={owners}
            onChange={(values) => {
              // Every alert must retain at least one owner.
              if ((values as string[]).length === 0) return
              setOwners(values as string[])
            }}
            options={allUsers.map((u) => ({ label: u, value: u }))}
            placeholder="Add owners…"
            aria-label="Alert owners"
          />
          <BoundsCaption style={{ marginTop: '8px' }}>
            Alerts must have at least one owner
          </BoundsCaption>
        </RowControlCol>
      </SummaryRow>

      <RowDivider />

      {/* ── Additional recipients ────────────────────────────────────────── */}
      <SummaryRow>
        <RowLabelCol>
          <RowLabel>Additional recipients</RowLabel>
          <RowSubLabel>
            Also receive the alert, but can&apos;t manage it
          </RowSubLabel>
        </RowLabelCol>
        <RowControlCol>
          <RecipientsSelect
            mode="multiple"
            value={recipients}
            onChange={(values) => setRecipients(values as string[])}
            options={allUsers.map((u) => ({ label: u, value: u }))}
            placeholder="Add additional recipients…"
            aria-label="Additional alert recipients"
          />
        </RowControlCol>
      </SummaryRow>

      <RowDivider />

      {/* ── Metric ───────────────────────────────────────────────────────── */}
      <SummaryRow>
        <RowLabelCol>
          <RowLabel>Metric</RowLabel>
          <RowSubLabel>What to watch</RowSubLabel>
        </RowLabelCol>
        <RowControlCol>
          <Controller
            name="alert_type"
            control={control}
            render={({ field }) => (
              <SegmentGroup>
                {alertTypeOptions.map((o) => (
                  <Segment
                    key={o.value}
                    $isSelected={field.value === o.value}
                    onClick={() => handleAlertTypeChange(o.value)}
                  >
                    {o.label}
                  </Segment>
                ))}
              </SegmentGroup>
            )}
          />
        </RowControlCol>
      </SummaryRow>

      <RowDivider />

      {/* ── Magnitude ────────────────────────────────────────────────────── */}
      <SummaryRow>
        <RowLabelCol>
          <RowLabel>Trigger threshold</RowLabel>
          <RowSubLabel>The values that trigger an alert</RowSubLabel>
        </RowLabelCol>
        <RowControlCol>
          <MagnitudeControls>
            {/* Segmented toggle */}
            <SegmentGroup>
              <Segment
                $isSelected={selectedTriggerCategory === 'outlier'}
                onClick={handleOutlierDetectionClick}
              >
                Outlier Detection
              </Segment>
              <Segment
                $isSelected={selectedTriggerCategory === 'limit'}
                onClick={handleLimitClick}
              >
                Custom Limits
              </Segment>
            </SegmentGroup>

            {/* Condition select + optional threshold */}
            <ConditionRow>
              <Controller
                name="trigger_type"
                control={control}
                render={({ field }) => (
                  <CardSelect
                    {...field}
                    onChange={(v) => handleAlertConditionChange(v as string)}
                    options={conditionOptions.map((o) => ({
                      value: o.value,
                      label: (alertType === 'Volume'
                        ? o.volume_label
                        : o.label
                      ).replace(/^./, (c) => c.toUpperCase()),
                    }))}
                    aria-label="Alert condition"
                    style={{ flex: 1 }}
                  />
                )}
              />
              {showCompareValue && (
                <Controller
                  name="compare_value"
                  control={control}
                  rules={{
                    required: true,
                    pattern: {
                      value: /^-?(\d+|\.\d+|\d+\.\d*)$/,
                      message: 'Please enter a number',
                    },
                    validate: {
                      starRatingRange: (value) => {
                        if (alertType !== 'Star Rating') return true
                        if (['Increased', 'Decreased'].includes(triggerType)) {
                          return (
                            (value >= 0 && value <= 4) ||
                            'Star Rating increase/decrease must be between 0 and 4'
                          )
                        }
                        return (
                          (value >= 1 && value <= 5) ||
                          'Star Rating must be between 1 and 5'
                        )
                      },
                      sentimentRange: (value) => {
                        if (alertType !== 'Sentiment') return true
                        if (['Increased', 'Decreased'].includes(triggerType)) {
                          return (
                            (value >= 0 && value <= 2) ||
                            'Sentiment increase/decrease must be between 0 and 2'
                          )
                        }
                        return (
                          (value >= -1 && value <= 1) ||
                          'Sentiment limit must be between -1 and 1'
                        )
                      },
                      volumeRange: (value) =>
                        alertType !== 'Volume' ||
                        value >= 1 ||
                        'Volume must be 1 or greater',
                    },
                  }}
                  render={({ field: { onChange, ...field } }) => {
                    const { min, max } = getCompareValueBounds(
                      alertType,
                      triggerType,
                    )
                    return (
                      <StyledNumberField
                        {...field}
                        min={min}
                        max={max}
                        onValueChange={(next) => {
                          onChange(next)
                          debouncedTrack('threshold', String(next ?? ''))
                        }}
                        placeholder="0"
                        aria-label="threshold"
                      />
                    )
                  }}
                />
              )}
            </ConditionRow>
            {showCompareValue && getMetricCaption(alertType) && (
              <BoundsCaption $isError={!!errors.compare_value}>
                {getMetricCaption(alertType)}
              </BoundsCaption>
            )}
          </MagnitudeControls>
        </RowControlCol>
      </SummaryRow>

      <RowDivider />

      {/* ── Time Period ───────────────────────────────────────────────────── */}
      <SummaryRow>
        <RowLabelCol>
          <RowLabel>Time period</RowLabel>
          <RowSubLabel>The period for which data is calculated</RowSubLabel>
        </RowLabelCol>
        <RowControlCol>
          <Controller
            name="time_interval"
            control={control}
            render={({ field }) => (
              <SegmentGroup>
                {intervalOptionsList.map((o) => (
                  <Segment
                    key={o.value}
                    $isSelected={field.value === o.value}
                    onClick={() => {
                      field.onChange(o.value)
                      mixpanel.track('alerts', {
                        action: 'time interval',
                        value: o.value,
                        proj_uuid: projectId,
                      })
                    }}
                  >
                    {o.label}
                  </Segment>
                ))}
              </SegmentGroup>
            )}
          />
        </RowControlCol>
      </SummaryRow>

      <RowDivider />

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <SummaryRow>
        <RowLabelCol>
          <RowLabel>Filters</RowLabel>
          <RowSubLabel>Narrow the data this alert watches</RowSubLabel>
        </RowLabelCol>
        <RowControlCol>
          <AlertDefinition
            filterState={filterState}
            setFilterState={setFilterState}
            filterOptions={filterOptions}
          />
        </RowControlCol>
      </SummaryRow>

      <RowDivider />

      {/* ── Minimum Review Count ─────────────────────────────────────────── */}
      <SummaryRow>
        <RowLabelCol>
          <RowLabel>Minimum record count</RowLabel>
          <RowSubLabel>
            Alerts only trigger if there are this many records in the period
          </RowSubLabel>
        </RowLabelCol>
        <RowControlCol>
          <Controller
            name="reviews_filter"
            control={control}
            render={({ field: { onChange, ...field } }) => (
              <StyledNumberField
                {...field}
                onValueChange={(next) => {
                  onChange(next)
                  debouncedTrack('minimum review count', String(next ?? ''))
                }}
                min={0}
              />
            )}
          />
        </RowControlCol>
      </SummaryRow>

      <RowDivider />

      {/* ── Preview ───────────────────────────────────────────────────────── */}
      <SummaryRow>
        <RowLabelCol>
          <RowLabel>Preview</RowLabel>
          <RowSubLabel>
            Number of times this alert would have triggered
          </RowSubLabel>
        </RowLabelCol>
        <RowControlCol>
          <AlertChartPreview
            alert_scope={alert_scope}
            alert_type={alertType}
            compare_value={compare_value}
            time_interval={time_interval}
            trigger_type={trigger_type}
            reviews_filter={reviews_filter}
            isFormValid={isFormValid}
            chartData={chartData}
            chartIsLoading={chartIsLoading}
          />
        </RowControlCol>
      </SummaryRow>

      <CardFooter>
        <SutraButton
          type="submit"
          $size="sm"
          $variant="primary"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? `${alert ? 'Updating' : 'Creating'}...`
            : `${alert ? 'Update' : 'Create'} alert`}
          {isSubmitting && <ButtonSpinner spin />}
        </SutraButton>
      </CardFooter>
    </SummaryCard>
  )
}

export default AlertSummaryCard

// ── Styled components ─────────────────────────────────────────────────────────

const SummaryCard = styled.div`
  background: white;
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.06);
  margin: 12px 0 16px;
  font-size: 13px;
`

const SummaryRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 18px 24px;
  gap: 24px;
`

const RowDivider = styled.div`
  border-top: 1px solid #f0f0f0;
  margin: 0 24px;
`

const RowLabelCol = styled.div`
  flex: 0 0 180px;
  padding-top: 2px;
`

const RowLabel = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #1a1a1a;
  margin-bottom: 2px;
`

const RowSubLabel = styled.div`
  font-size: 13px;
  color: #888;
`

const RowControlCol = styled.div`
  flex: 1;
  min-width: 0;
`

const TitleError = styled(ErrorMessage)`
  margin-top: 4px;
  font-size: 13px;
`

const RecipientsSelect = styled(Select)`
  width: 100%;
` as typeof Select

/** Clean white single-select for Metric / Magnitude / Time Period rows */
const CardSelect = styled(Select)`
  min-width: 0;
  && {
    height: 38px;
    border-color: #d9d9d9;
    border-radius: 8px;
  }
` as typeof Select

const MagnitudeControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SegmentGroup = styled.div`
  display: flex;
  width: 100%;
`

const Segment = styled(CustomButtonGroupItem)`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:not(:first-child) {
    margin-left: -1px;
  }
  &:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }
  &:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`

const ConditionRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`

const BoundsCaption = styled.div<{ $isError?: boolean }>`
  font-size: 11px;
  color: ${({ $isError }) => ($isError ? 'red' : '#888')};
`

const CardFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 18px 24px;
  border-top: 1px solid #f0f0f0;
`
